import { useCallback, useEffect, useRef } from 'react';

interface Grecaptcha {
  ready: (cb: () => void) => void;
  render: (
    container: HTMLElement,
    params: {
      sitekey: string;
      size: 'invisible';
      callback: (token: string) => void;
      'error-callback': () => void;
    },
  ) => number;
  execute: (widgetId: number) => void;
  reset: (widgetId: number) => void;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

let scriptPromise: Promise<Grecaptcha> | null = null;

const loadRecaptcha = (): Promise<Grecaptcha> => {
  if (!scriptPromise) {
    scriptPromise = new Promise<Grecaptcha>((resolve, reject) => {
      const whenReady = () => window.grecaptcha?.ready(() => resolve(window.grecaptcha as Grecaptcha));
      if (window.grecaptcha?.render) {
        whenReady();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      script.async = true;
      script.onload = whenReady;
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('reCAPTCHA failed to load'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

/**
 * Invisible reCAPTCHA v2, the same widget the landing page signup uses. Attach `containerRef` to an element that
 * stays mounted while the form is open; `execute` resolves with a fresh token for each submit.
 */
export const useInvisibleRecaptcha = (siteKey: string) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<number | null>(null);
  const pendingRef = useRef<{ resolve: (token: string) => void; reject: (error: Error) => void } | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadRecaptcha()
      .then((grecaptcha) => {
        if (cancelled || !containerRef.current || widgetRef.current !== null) return;
        widgetRef.current = grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          size: 'invisible',
          callback: (token) => {
            pendingRef.current?.resolve(token);
            pendingRef.current = null;
          },
          'error-callback': () => {
            // Google gives no reason here; on localhost it is usually a site key that doesn't list this domain.
            console.warn('reCAPTCHA failed. Check that the site key is valid and allows this domain.');
            pendingRef.current?.reject(new Error('reCAPTCHA error'));
            pendingRef.current = null;
          },
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      widgetRef.current = null;
    };
  }, [siteKey]);

  const execute = useCallback(async (): Promise<string> => {
    const grecaptcha = await loadRecaptcha();
    const widgetId = widgetRef.current;
    if (widgetId === null) throw new Error('reCAPTCHA is not ready');
    grecaptcha.reset(widgetId);
    return new Promise<string>((resolve, reject) => {
      pendingRef.current = { resolve, reject };
      grecaptcha.execute(widgetId);
    });
  }, []);

  return { containerRef, execute };
};
